import { XProtectVersions, ServerSettingsData, StorageSetupDatabase } from 'lib/types'
import { useState, useEffect } from 'react'
import { checkNumberOfCamerasExceedingManagementServerSelection } from '../ServerSettingsService'

type GenericServerSettingsDataSettings = {
  genericformData: any
  genericAddManagementServer: boolean
  hasGenericArchiving: boolean
  xProtectPlatforms: { label: string; value: string | number }[]
  genericXProtectVersion: number
  genericSystemOverhead: number
  genericXProtectVersionCameraMaxQty: number
  genericXProtectVersionCameraQty: number
  genericConcurrentSmartClientStreams: number
  machineTypes: { [key: string]: string }
  genericMachineType: number
  serverDiskQuantities: { [key: string]: string }
  genericServerDiskQuantity: number
  raiD1ForOSOptions: { [key: string]: string }
  genericRaiD1ForOSOption: boolean
  archiveFrequencies: { [key: string]: string }
  genericArchiveFrequency: number
  supportedCameras: { [key: string]: string }
  genericSupportedCamera: number
  genericLiveDatase: StorageSetupDatabase
  genericArchiveDatabase: StorageSetupDatabase
  isNumberOfCamerasExceedingManagementServerSelection: boolean
  handleGenericXProtectPlatformChange
  handleGenericValueChange
  handleGenericArchivingValueChange
  handleGenericStorageSetupValueChange
  handleGenericSupportedCamerasValueChange
}

export const useGenericServerSettingsData = (
  formData,
  serverSettings: ServerSettingsData,
  camerasAmount: number,
  handleXProtectPlatformChange,
  handleValueChange,
  handleArchivingValueChange,
  handleStorageSetupValueChange,
  handleSupportedCamerasValueChange
): GenericServerSettingsDataSettings => {
  const genericServerSettings = serverSettings?.genericServerSettings
  const genericformData = formData
  const genericXProtectVersionsData: XProtectVersions = genericformData?.xProtectVersions ?? {}
  let xProtectPlatforms: { label: string; value: string | number }[] = Object.entries(genericXProtectVersionsData)
    .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
    .map(([key, version]) => ({
      label: version.name,
      value: key
    }))

  const genericAddManagementServer = genericServerSettings?.addManagementServer

  const genericXProtectVersion = genericServerSettings?.xProtectVersion ?? 4 //initial value should be the XProtect Professional+ == 4
  const genericXProtectVersionCameraMaxQty = genericXProtectVersionsData[genericXProtectVersion]?.camQtyMax
  let genericXProtectVersionCameraQty = genericServerSettings?.mainSettings?.maxCamerasPerServer ?? 0
  if (genericXProtectVersionCameraQty === 0) {
    genericXProtectVersionCameraQty = genericXProtectVersionsData[genericXProtectVersion]?.camQtyDefault
  }

  const genericSystemOverhead = genericServerSettings?.mainSettings?.systemOverhead ?? 0
  const genericConcurrentSmartClientStreams = genericServerSettings?.mainSettings?.concurrentSmartClientStreams ?? 0

  const machineTypes = genericformData?.machineTypes ?? { key: '' }
  const genericMachineType = genericServerSettings?.advancedSettings?.machineType ?? 0

  const serverDiskQuantities = genericformData?.serverDiskQuantities ?? { key: '' }
  const genericServerDiskQuantity = genericServerSettings?.advancedSettings?.serverDiskQuantity ?? 0

  const raiD1ForOSOptions = genericformData?.raidOS ?? { key: '' }
  const genericRaiD1ForOSOption = genericServerSettings?.advancedSettings?.raidOS ?? false

  const archiveFrequencies = genericformData?.archiveFrequencies ?? { key: '' }
  const genericArchiveFrequency = genericServerSettings?.advancedSettings?.archiveFrequency ?? 0

  const supportedCameras = genericformData?.supportedCameras ?? { key: '' }
  const genericSupportedCamera = genericServerSettings?.managementServerSettings?.supportedCameras ?? 0

  const hasGenericArchiving = genericServerSettings?.advancedSettings?.archiving
  const genericLiveDatase = genericServerSettings?.advancedSettings?.liveDatabase
  const genericArchiveDatabase = genericServerSettings?.advancedSettings?.archiveDatabase

  //Handles the XProtect Platform change and checks if we need to reinitialize the "add management server" property as well based on the XProtectplatform selection
  const handleGenericXProtectPlatformChange = () => (xProtectPlatformId: number) => {
    let updatedServerSettings: ServerSettingsData = {
      ...serverSettings,
      genericServerSettings: {
        ...genericServerSettings,
        xProtectVersion: xProtectPlatformId
      }
    }

    //Check if we need to update the management server value if it is express+
    if (xProtectPlatformId === 3) {
      updatedServerSettings = {
        ...updatedServerSettings,
        genericServerSettings: {
          ...updatedServerSettings.genericServerSettings,
          addManagementServer: false
        }
      }
    }

    const xProtectVersionCameraDefaultQty = genericformData?.xProtectVersions[xProtectPlatformId]?.camQtyDefault
    updatedServerSettings = {
      ...updatedServerSettings,
      genericServerSettings: {
        ...updatedServerSettings.genericServerSettings,
        mainSettings: {
          ...updatedServerSettings.genericServerSettings.mainSettings,
          maxCamerasPerServer: xProtectVersionCameraDefaultQty
        }
      }
    }

    handleXProtectPlatformChange(updatedServerSettings)
  }

  //Handling most of the element value change
  const handleGenericValueChange = propertyPath => (value: boolean | string | number) => {
    handleValueChange(propertyPath, value)
  }

  const handleGenericArchivingValueChange = propertyPath => (value: boolean) => {
    handleArchivingValueChange(propertyPath, value)
  }

  //Specific event to handle when the live database or archiving database properties are changed.
  const handleGenericStorageSetupValueChange = propertyPath => (propertyName: string, value: number) => {
    if (propertyName) {
      handleStorageSetupValueChange(propertyPath, propertyName, value, true)
    }
  }

  //Handling the management server
  useEffect(() => {
    const hasExceeded = checkNumberOfCamerasExceedingManagementServerSelection(
      serverSettings?.genericServerSettings?.addManagementServer,
      serverSettings?.genericServerSettings?.managementServerSettings?.supportedCameras,
      camerasAmount
    )
    setIsNumberOfCamerasExceedingManagementServerSelection(hasExceeded)
  }, [camerasAmount, serverSettings])

  const [isNumberOfCamerasExceedingManagementServerSelection, setIsNumberOfCamerasExceedingManagementServerSelection] = useState(false)
  const handleGenericSupportedCamerasValueChange = () => (value: number) => {
    let updatedGenericServerSettings: ServerSettingsData = {
      ...serverSettings,
      genericServerSettings: {
        ...genericServerSettings,
        managementServerSettings: {
          supportedCameras: value
        }
      }
    }

    handleSupportedCamerasValueChange(updatedGenericServerSettings)
    setIsNumberOfCamerasExceedingManagementServerSelection(
      checkNumberOfCamerasExceedingManagementServerSelection(
        updatedGenericServerSettings?.genericServerSettings?.addManagementServer,
        updatedGenericServerSettings?.genericServerSettings?.managementServerSettings?.supportedCameras,
        camerasAmount
      )
    )
  }
  //End of handling the management server

  return {
    genericformData,
    genericAddManagementServer,
    xProtectPlatforms,
    genericXProtectVersion,
    genericXProtectVersionCameraMaxQty,
    genericXProtectVersionCameraQty,
    genericSystemOverhead,
    genericConcurrentSmartClientStreams,
    machineTypes,
    genericMachineType,
    archiveFrequencies,
    genericArchiveFrequency,
    genericServerDiskQuantity,
    raiD1ForOSOptions,
    genericRaiD1ForOSOption,
    serverDiskQuantities,
    supportedCameras,
    genericSupportedCamera,
    genericLiveDatase,
    hasGenericArchiving,
    genericArchiveDatabase,
    isNumberOfCamerasExceedingManagementServerSelection,
    handleGenericXProtectPlatformChange,
    handleGenericValueChange,
    handleGenericArchivingValueChange,
    handleGenericStorageSetupValueChange,
    handleGenericSupportedCamerasValueChange
  }
}
